/**
 * O hook de identidade.
 *
 * A sessão não é estado de componente: ela é única na aba, sobrevive a
 * desmontagens e é lida por partes da árvore que não se conhecem. Por isso o
 * dado mora no módulo (`authClient`) e o hook apenas o observa por
 * `useSyncExternalStore` — assim dois componentes que chamem `useAuth()` veem o
 * mesmo principal no mesmo render, sem provider, sem contexto e sem o risco
 * clássico de duas cópias divergentes da mesma verdade.
 *
 * O `principal` é sempre o que o servidor resolveu. Não há função aqui para
 * escolher tenant, nem para editá-lo: se ela existisse, alguém a usaria.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  assinarSessao,
  entrar,
  estadoDaSessao,
  restaurarSessao,
  sair,
  type AuthPhase,
  type AuthState,
  type PedidoDeLogin,
  type Principal,
} from './authClient';

export interface Auth {
  /** quem o servidor disse que somos; `null` enquanto não há sessão */
  readonly principal: Principal | null;
  /** restaurando a sessão ou com requisição de identidade em voo */
  readonly loading: boolean;
  /** última falha, em português; `null` quando não há o que contar */
  readonly error: string | null;
  /**
   * Entra. Resolve `true` quando a sessão nasceu; em caso de falha resolve
   * `false` — o motivo já está em `error`. Não rejeita de propósito: um portão
   * de login que estoura uma promise não tratada por senha errada transforma
   * erro de usuário em erro de programa.
   */
  readonly login: (pedido: PedidoDeLogin) => Promise<boolean>;
  readonly logout: () => Promise<void>;

  // -- derivados, para quem consome não recalcular -------------------------
  readonly autenticado: boolean;
  readonly fase: AuthPhase;
  /** papéis vêm resolvidos do servidor; isto é só leitura conveniente */
  readonly temPapel: (papel: string) => boolean;
}

/**
 * A restauração dispara uma vez por carga de página.
 *
 * O `StrictMode` monta, desmonta e remonta em desenvolvimento (ver `main.tsx`);
 * sem esta trava o boot faria dois `GET /auth/me`. A trava é de módulo, e não
 * de ref, justamente porque precisa atravessar a desmontagem.
 */
let restauracaoIniciada = false;

function garantirRestauracao(): void {
  if (restauracaoIniciada) return;
  restauracaoIniciada = true;
  void restaurarSessao();
}

const assinar = (aoMudar: () => void): (() => void) => assinarSessao(aoMudar);

/** O snapshot é o próprio objeto do módulo: só troca de referência quando muda. */
const snapshot = (): AuthState => estadoDaSessao();

export function useAuth(): Auth {
  const estado = useSyncExternalStore(assinar, snapshot, snapshot);

  useEffect(garantirRestauracao, []);

  const login = useCallback(async (pedido: PedidoDeLogin): Promise<boolean> => {
    try {
      await entrar(pedido);
      return true;
    } catch {
      // `entrar` já publicou a mensagem no estado; repeti-la aqui só criaria
      // uma segunda fonte da mesma verdade.
      return false;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await sair();
  }, []);

  const temPapel = useCallback(
    (papel: string): boolean => estado.principal?.roles.includes(papel) ?? false,
    [estado.principal],
  );

  return {
    principal: estado.principal,
    loading: estado.fase === 'restaurando' || estado.ocupado,
    error: estado.error,
    login,
    logout,
    autenticado: estado.fase === 'autenticado' && estado.principal !== null,
    fase: estado.fase,
    temPapel,
  };
}
